/* eslint-disable no-extend-native */
import { Suspense, lazy, useEffect } from 'react';
import { QueryClient, QueryClientProvider } from 'react-query';
import { Provider } from 'react-redux';
import 'react-redux-toastr/lib/css/react-redux-toastr.min.css';
import { BrowserRouter, Route, Routes } from 'react-router-dom';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

import * as amplitude from '@amplitude/analytics-browser';
import GroupOperationsMonitor from 'components/GroupOperationsMonitor';
import SpinnerView from 'components/SpinnerView';
import { BASE_URLS } from 'constants/urls';
import { CallInProgressProvider } from 'features/Yucollect/contexts/CallInProgressContext';
import GlobalErrorModal from 'features/Yucollect/shared/GlobalErrorModal';
import {
  isAgencyOwner,
  isAgencySupervisor,
  isSupervisor,
} from 'features/Yucollect/utils/util';
import {
  LazyAgencyCreated,
  LazyAgencyOnboardingV2,
  LazyAgencyProfile,
  LazyAgencyProfileAllocatorDocument,
  LazyAgencyProfileDiscovery,
  LazyAgencyProfileYucollectDocument,
  LazyAgencySignIn,
  LazyAgencySignUp,
  LazyProfileRegistered,
  LazyRegister,
  LazySignUp,
  LazyVendorDetail,
} from 'navigation/LazyComponents';
import ProtectedRoute from 'navigation/ProtectedRoutes';
import { PersistGate } from 'redux-persist/integration/react';
import { fetchAgentInfo } from 'redux/slice/crm/CRM.slice';
import Smartlook from 'smartlook-client';
import { useAppDispatch } from 'utils/hooks/useAppDispatch';
import { useAppSelector } from 'utils/hooks/useAppSelector';

import { AuthService, AuthWrapper } from '@yubi/yb-module-auth';

import { GlobalStyles, RootContainer, StyledReduxToaster } from './App.styled';
import { LayoutProvider } from './context/LayoutContext';
import { persistor, store } from './redux/store/store';
import './styles/index.css';
import './styles/tailwind.css';

const queryClient = new QueryClient();

const Navigation = lazy(() => import('./navigation/Navigation'));
const PersistentTubelight = lazy(
  () => import('./components/PersistentTubelight')
);

export const AuthMain = () => {
  const { userRole = '' } = useAppSelector((state) => state.userConfiguration);
  const { caUserId: AGENT_ID } = useAppSelector(
    (state) => state.userConfiguration
  );
  const dispatch = useAppDispatch();

  // This effect will run once after login and when the page refreshes
  useEffect(() => {
    // Function to check auth state and fetch agent info if needed
    const checkAuthAndFetchAgentInfo = () => {
      try {
        const token = AuthService.getAccessToken();
        const isLoggedIn = AuthService.getActiveUserGroup() !== null;

        if (
          isLoggedIn &&
          token &&
          AGENT_ID &&
          (userRole === 'cc_agent' || userRole === 'cc_supervisor')
        ) {
          dispatch(
            fetchAgentInfo({
              agentId: AGENT_ID,
            })
          );
        }
      } catch (error) {
        console.error('Error checking auth state:', error);
      }
    };

    checkAuthAndFetchAgentInfo();

    // Add event listener for page refresh (when the page becomes visible again after refresh)
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        checkAuthAndFetchAgentInfo();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [dispatch, AGENT_ID, userRole]);

  return (
    <AuthWrapper
      organizationDetails={{
        organisationId: process.env.REACT_APP_ORGANIZATION_ID || '',
        imagePayload: {
          alt: 'yucollect logo',
          src: `${BASE_URLS.ASSETS}/assets/Logo_Yucollectsvg.svg`,
          style: {
            width: '135px',
            display: 'unset',
            height: '30px',
          },
        },
        email: process.env.REACT_APP_SUPPORT_EMAIL || 'help@yucollect.com',
        supportMessage:
          'If you have trouble verifying your email, please contact Yucollect Support',
      }}
      navigator={() => {}}
      amplitudeApiKey={process.env.REACT_APP_AMPLITUDE_API_KEY || undefined}
    >
      <CallInProgressProvider>
        <Suspense fallback={<SpinnerView label="Loading..." />}>
          <Navigation />
        </Suspense>
        {/* Global error modal mounted once for the entire authenticated app */}
        <GlobalErrorModal />
        {(userRole === 'cc_agent' || userRole === 'cc_supervisor') && (
          <Suspense fallback={<SpinnerView label="Loading..." />}>
            <PersistentTubelight />
          </Suspense>
        )}
        {/* Group Operations Monitor for supervisors only */}
        {(isSupervisor(userRole) ||
          isAgencyOwner(userRole) ||
          isAgencySupervisor(userRole)) && <GroupOperationsMonitor />}
      </CallInProgressProvider>
    </AuthWrapper>
  );
};

const App = () => {
  const isLoggedIn = AuthService.getActiveUserGroup() !== null;

  useEffect(() => {
    Smartlook.init(process.env.REACT_APP_SMART_LOOK_ANALYTICS || '');
  }, []);

  useEffect(() => {
    if (process.env.REACT_APP_AMPLITUDE_API_KEY) {
      amplitude.init(process.env.REACT_APP_AMPLITUDE_API_KEY, {
        autocapture: false,
      });
    }
  }, []);

  const isSignUpPage = location.pathname.includes('old-agency/sign-up');
  const isSignInPage = location.pathname.includes('old-agency/sign-in');
  const isAgencyV2SignUpPage = location.pathname.includes('agency/sign-up');
  const isAgencyV2RegisterPage = location.pathname.includes('agency/register');
  const profileRegistered = location.pathname.includes(
    'agency/profile-registered'
  );
  const basicDetails = location.pathname.includes('agency/basic-details');
  const isAgencyDiscover = location.pathname.includes('old-agency/discovery');
  const isAgencyYucollectDocuments = location.pathname.includes(
    'old-agency/yucollect-documents'
  );
  const isAgencyAllocatorsDocuments = location.pathname.includes(
    'old-agency/allocator-documents'
  );
  const isAgencyProfile = location.pathname.includes('old-agency/profile');
  const agencyCreated = location.pathname.includes('agency/agency-created');
  const agencyProfile = location.pathname.includes('agency/profile');

  return (
    <RootContainer>
      <ToastContainer />
      <GlobalStyles />
      <Provider store={store}>
        <StyledReduxToaster
          timeOut={4000}
          newestOnTop={false}
          preventDuplicates
          position="top-right"
          transitionIn="fadeIn"
          transitionOut="fadeOut"
          progressBar={false}
        />
        <PersistGate loading={null} persistor={persistor}>
          <QueryClientProvider client={queryClient}>
            <LayoutProvider>
              {!isLoggedIn &&
              (isSignUpPage ||
                isSignInPage ||
                isAgencyDiscover ||
                isAgencyYucollectDocuments ||
                isAgencyAllocatorsDocuments ||
                isAgencyV2SignUpPage ||
                isAgencyV2RegisterPage ||
                profileRegistered ||
                basicDetails ||
                agencyCreated ||
                agencyProfile ||
                isAgencyProfile) ? (
                <BrowserRouter>
                  <Suspense fallback={<SpinnerView label="Loading..." />}>
                    <Routes>
                      <Route path="agency/sign-up" element={<LazySignUp />} />
                      <Route
                        path="agency/register"
                        element={<LazyRegister />}
                      />
                      <Route
                        path="agency/profile-registered"
                        element={<LazyProfileRegistered />}
                      />
                      <Route
                        path="agency/basic-details"
                        element={<LazyAgencyOnboardingV2 />}
                      />
                      <Route
                        path="agency/agency-created"
                        element={<LazyAgencyCreated />}
                      />
                      <Route
                        path="agency/profile"
                        element={<LazyAgencyProfile isNonAuth={true} />}
                      />
                      <Route
                        path="old-agency/sign-up"
                        element={<LazyAgencySignUp />}
                      />
                      <Route
                        path="old-agency/sign-in"
                        element={<LazyAgencySignIn />}
                      />
                      <Route
                        path="old-agency/discovery"
                        element={
                          <LazyAgencyProfileDiscovery isNonAuth={true} />
                        }
                      />
                      <Route
                        path="old-agency/yucollect-documents"
                        element={
                          <ProtectedRoute>
                            <LazyAgencyProfileYucollectDocument
                              isNonAuth={true}
                            />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="old-agency/allocator-documents/:agencyAllocatorId"
                        element={
                          <LazyAgencyProfileAllocatorDocument
                            isNonAuth={true}
                          />
                        }
                      />
                      <Route
                        path="old-agency/profile/:vendorId"
                        element={<LazyVendorDetail isNonAuth={true} />}
                      />
                    </Routes>
                  </Suspense>
                </BrowserRouter>
              ) : (
                <Suspense fallback={<SpinnerView label="Loading..." />}>
                  <AuthMain />
                </Suspense>
              )}
            </LayoutProvider>
          </QueryClientProvider>
        </PersistGate>
      </Provider>
    </RootContainer>
  );
};

export default App;
